import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Component({
    selector: 'lokos-table',
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
    standalone: false,
})
export class TableComponent implements OnChanges, AfterViewInit {
    @Input() tableRecord: any[] = [];
    @Input() columns: any[] = [];
    @Input({ required: true }) entity: any;
    @Input() selectedRow: any = [];
    @Input() activePageIndex = 0;
    @Input() pageSize = 10;
    @Input() stickyFirst = false;
    @Input() noDataFoundText = 'noRecord';
    @Input() showTotalRow = false;
    @Input() totalRowData: any = {};
    @Input() totalLabel = 'total';
    @Input() totalColspan = 2;
    @Input() sortFieldMap: Record<string, string> = {};
    @Input() footerLeft?: string;
    @Input() footerMiddle?: string;
    @Input() footerRight?: string;

    @Output() onSortingChange = new EventEmitter<any>();
    @Output() onRowClick = new EventEmitter<any>();
    @Output() onSelectionChange = new EventEmitter<any>();
    @Output() customColumnEvent = new EventEmitter<any>();
    @Output() customActionEvent = new EventEmitter<any>();
    @Output() toggleSpecificRows = new EventEmitter<any>();

    @ViewChild(MatSort) sort?: MatSort;

    displayColumns: any[] = [];
    dataSource = new MatTableDataSource(this.tableRecord);
    selection = new SelectionModel<any>(true, []);

    // Two-level header signals (existing)
    groupedColumns = signal<any[]>([]);
    groupHeaderRow = signal<any[]>([]);
    topHeaderFields = signal<string[]>([]);
    subHeaderFields = signal<string[]>([]);
    hasGroupHeaders = signal(false);

    // Three-level header signals (new)
    hasSuperGroupHeaders = signal(false);
    superGroupHeaderRow = signal<any[]>([]);
    superGroupFields = signal<string[]>([]);

    tableReady = signal(true);

    ngOnChanges(changes: SimpleChanges) {
        if (changes['tableRecord']) {
            this.dataSource = new MatTableDataSource(this.tableRecord);
            this.selection.clear();
            this.attachSort();
        }

        if (changes['columns']) {
            const prevHasGroup = this.hasGroupHeaders();
            const prevHasSuper = this.hasSuperGroupHeaders();
            const prevSuperFields = JSON.stringify(this.superGroupFields());
            const prevTopFields = JSON.stringify(this.topHeaderFields());
            const prevSubFields = JSON.stringify(this.subHeaderFields());
            this.prepareColumnForShow();
            this.createGroupHeaders();
            if (
                prevHasGroup !== this.hasGroupHeaders() ||
                prevHasSuper !== this.hasSuperGroupHeaders() ||
                prevSuperFields !== JSON.stringify(this.superGroupFields()) ||
                prevTopFields !== JSON.stringify(this.topHeaderFields()) ||
                prevSubFields !== JSON.stringify(this.subHeaderFields())
            ) {
                this.tableReady.set(false);
                Promise.resolve().then(() => this.tableReady.set(true));
            }
        }

        if (changes['selectedRow']) {
            this.selection = new SelectionModel<any>(true, this.selectedRow);
        }

        if (changes['sortFieldMap']) {
            this.applySortAccessor();
        }
    }

    ngAfterViewInit() {
        this.attachSort();
    }

    private attachSort() {
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        this.applySortAccessor();
        this.applyPinnedTotalSort();
    }

    private applyPinnedTotalSort() {
        const baseSort = this.dataSource.sortData.bind(this.dataSource);
        this.dataSource.sortData = (data: any[], sort: MatSort) => {
            const pinned = data.filter((r: any) => r?.isTotal);
            const regular = data.filter((r: any) => !r?.isTotal);
            return [...baseSort(regular, sort), ...pinned];
        };
    }

    private applySortAccessor() {
        this.dataSource.sortingDataAccessor = (row: any, columnId: string) => {
            const key = this.sortFieldMap?.[columnId] || columnId;
            const value = row?.[key];
            if (value === null || value === undefined || value === '') {
                return '';
            }
            if (typeof value === 'string') {
                const dateMatch = value.match(
                    /^(\d{2})[-/](\d{2})[-/](\d{4})$/
                );
                if (dateMatch) {
                    const [, dd, mm, yyyy] = dateMatch;
                    return new Date(`${yyyy}-${mm}-${dd}`).getTime();
                }
                return value.toLowerCase();
            }
            return value;
        };
    }

    prepareColumnForShow() {
        this.displayColumns = [];
        this.columns?.forEach((obj: any) => {
            this.displayColumns.push(obj.field);
        });
        this.displayColumns = [...this.displayColumns];
    }

    private createGroupHeaders() {
        if (!this.columns?.length) {
            this.hasGroupHeaders.set(false);
            this.hasSuperGroupHeaders.set(false);
            this.groupHeaderRow.set([]);
            this.topHeaderFields.set([]);
            this.subHeaderFields.set([]);
            this.groupedColumns.set([]);
            this.superGroupHeaderRow.set([]);
            this.superGroupFields.set([]);
            return;
        }

        const hasAnyGroup = this.columns.some((col: any) => col.group);
        const hasAnySuperGroup = this.columns.some(
            (col: any) => col.superGroup
        );

        this.hasGroupHeaders.set(hasAnyGroup);
        this.hasSuperGroupHeaders.set(hasAnySuperGroup);

        if (!hasAnyGroup) {
            this.groupHeaderRow.set([]);
            this.topHeaderFields.set([]);
            this.subHeaderFields.set([]);
            this.groupedColumns.set([]);
            this.superGroupHeaderRow.set([]);
            this.superGroupFields.set([]);
            return;
        }

        // ── Legacy groupedColumns (backward compat) ──────────────────────
        const groups: any = {};
        this.columns.forEach((col: any) => {
            if (col.group) {
                if (!groups[col.group]) groups[col.group] = [];
                groups[col.group].push(col.field);
            }
        });
        this.groupedColumns.set(
            Object.keys(groups).map((label) => ({
                label,
                colspan: groups[label].length,
            }))
        );

        if (hasAnySuperGroup) {
            const superRow: any[] = [];
            const superFields: string[] = [];
            const midRow: any[] = [];
            const midFields: string[] = [];
            const subFields: string[] = [];
            let lastSuperGroup: string | null = null;
            let lastTopGroup: string | null = null;
            let lastInnerGroup: string | null = null;

            this.columns.forEach((col: any) => {
                const sg = col.superGroup || null;
                const g =
                    col.group && col.group !== 'Others' ? col.group : null;

                if (sg) {
                    lastTopGroup = null;
                    if (sg !== lastSuperGroup) {
                        lastSuperGroup = sg;
                        lastInnerGroup = null;
                        const sgColCount = this.columns.filter(
                            (c: any) => c.superGroup === sg
                        ).length;
                        const sgField =
                            'super_' + sg.replace(/[^a-zA-Z0-9]/g, '_');
                        superRow.push({
                            field: sgField,
                            label: sg,
                            colspan: sgColCount,
                            rowspan: 1,
                            isSuperGroup: true,
                        });
                        superFields.push(sgField);
                    }
                    if (g) {
                        if (g !== lastInnerGroup) {
                            lastInnerGroup = g;
                            const gColCount = this.columns.filter(
                                (c: any) => c.superGroup === sg && c.group === g
                            ).length;
                            const gField =
                                'group_' +
                                sg.replace(/[^a-zA-Z0-9]/g, '_') +
                                '_' +
                                g.replace(/[^a-zA-Z0-9]/g, '_');
                            midRow.push({
                                field: gField,
                                label: g,
                                colspan: gColCount,
                                rowspan: 1,
                                isGroup: true,
                            });
                            midFields.push(gField);
                        }
                        subFields.push(col.field);
                    } else {
                        const mField = 'mid_' + col.field;
                        midRow.push({
                            field: mField,
                            label: col.label,
                            colspan: 1,
                            rowspan: 2,
                            isGroup: false,
                        });
                        midFields.push(mField);
                    }
                } else if (g) {
                    lastSuperGroup = null;
                    lastInnerGroup = null;
                    if (g !== lastTopGroup) {
                        lastTopGroup = g;
                        const gColCount = this.columns.filter(
                            (c: any) =>
                                !c.superGroup &&
                                c.group &&
                                c.group !== 'Others' &&
                                c.group === g
                        ).length;
                        const gField =
                            'topgroup_' + g.replace(/[^a-zA-Z0-9]/g, '_');
                        superRow.push({
                            field: gField,
                            label: g,
                            colspan: gColCount,
                            rowspan: 1,
                            isSuperGroup: false,
                            isGroup: true,
                        });
                        superFields.push(gField);
                    }
                    const mField = 'mid_' + col.field;
                    midRow.push({
                        field: mField,
                        label: col.label,
                        colspan: 1,
                        rowspan: 2,
                        isGroup: false,
                    });
                    midFields.push(mField);
                } else {
                    lastSuperGroup = null;
                    lastInnerGroup = null;
                    lastTopGroup = null;
                    const tField = 'supertop_' + col.field;
                    superRow.push({
                        field: tField,
                        label: col.label,
                        colspan: 1,
                        rowspan: 3,
                        isSuperGroup: false,
                    });
                    superFields.push(tField);
                }
            });

            this.superGroupHeaderRow.set(superRow);
            this.superGroupFields.set(superFields);
            this.groupHeaderRow.set(midRow);
            this.topHeaderFields.set(midFields);
            this.subHeaderFields.set(subFields);
            return;
        }

        // ── 2-row layout (no super-group) ────────────────────────────────
        const topRow: any[] = [];
        const topFields: string[] = [];
        const subFields: string[] = [];
        let lastGroup: string | null = null;

        this.columns.forEach((col: any) => {
            const isUngrouped = !col.group || col.group === 'Others';

            if (!isUngrouped) {
                if (col.group !== lastGroup) {
                    lastGroup = col.group;
                    const groupColCount = this.columns.filter(
                        (c: any) => c.group === col.group
                    ).length;
                    const groupField =
                        'group_' + col.group.replace(/[^a-zA-Z0-9]/g, '_');

                    topRow.push({
                        field: groupField,
                        label: col.group,
                        colspan: groupColCount,
                        rowspan: 1,
                        isGroup: true,
                        sort: col.sort,
                    });
                    topFields.push(groupField);
                }
                subFields.push(col.field);
            } else {
                lastGroup = null;
                const topField = 'top_' + col.field;
                topRow.push({
                    field: topField,
                    label: col.label,
                    colspan: 1,
                    rowspan: 2,
                    isGroup: false,
                    sort: col.sort,
                });
                topFields.push(topField);
            }
        });

        this.groupHeaderRow.set(topRow);
        this.topHeaderFields.set(topFields);
        this.subHeaderFields.set(subFields);
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        if (this.entity === 'potential_didi_approve_component') {
            const numSelected = this.selection.selected.length;
            const numRows = this.dataSource.data.filter(
                (member) => member.ldd_status === 1
            ).length;
            return numSelected === numRows;
        }
        if (this.entity === 'potential_didi_verification_component') {
            const numSelected = this.selection.selected.length;
            const numRows = this.dataSource.data.filter(
                (member) => member.approvalStatus === 'Approved'
            ).length;
            return numSelected === numRows;
        }
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    toggleAllRows() {
        if (this.isAllSelected()) {
            this.selection.clear();
            this.onSelectionChange.emit(this.selection.selected);
            return;
        }
        if (this.entity === 'potential_didi_approve_component') {
            const selectedMembers = this.dataSource.data.filter((member) => {
                return member.ldd_status === 1;
            });
            this.selection.select(...selectedMembers);
            this.onSelectionChange.emit(this.selection.selected);
        } else if (this.entity === 'potential_didi_verification_component') {
            const selectedMembers = this.dataSource.data.filter((member) => {
                return member.approvalStatus === 'Approved';
            });
            this.selection.select(...selectedMembers);
            this.onSelectionChange.emit(this.selection.selected);
        } else {
            this.selection.select(...this.dataSource.data);
            this.onSelectionChange.emit(this.selection.selected);
        }
    }

    toggleRowsSelection(row: any) {
        this.selection.toggle(row);
        this.onSelectionChange.emit(this.selection.selected);
    }

    disableCheckbox(member: any) {
        if (this.entity === 'potential_didi_approve_component') {
            if (member.ldd_status !== 1) return true;
        } else if (this.entity === 'potential_didi_verification_component') {
            if (member.approvalStatus !== 'Approved') return true;
        }
        return false;
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: any): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
    }

    rowClick(row: any) {
        this.onRowClick.emit(row);
    }

    sortData(event: any) {
        this.onSortingChange.emit(event);
    }

    get mappedGroupedColumns() {
        return this.groupedColumns().map((g) => g.label);
    }

    get hasFooterStrip(): boolean {
        return !!(this.footerLeft || this.footerMiddle || this.footerRight);
    }
}
